import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { Y42FeatureType } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { FeatureAggregationService } from '../../api';
import { AbstractFeatureChildUIModel, AbstractFeatureUIModel } from '../../models';
import { AssetSidebarTabType } from './asset-sidebar-tab.model';

@UntilDestroy()
@Injectable()
export class AssetSidebarService<
  Asset extends AbstractFeatureUIModel | AbstractFeatureChildUIModel =
    | AbstractFeatureUIModel
    | AbstractFeatureChildUIModel,
  Params = object,
> {
  constructor(
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private readonly readOnly$$ = new BehaviorSubject(true);
  private readonly loading$$ = new BehaviorSubject(true);
  private readonly assetPath$$ = new BehaviorSubject<string | null>(null);
  private readonly activeTabType$$ = new BehaviorSubject<AssetSidebarTabType>(AssetSidebarTabType.Overview);
  private readonly params$$ = new BehaviorSubject<Partial<Params>>({} as Params);

  assetPath: string | null = null;
  readonly assetPath$: Observable<string | null> = this.assetPath$$;

  readonly featureType$: Observable<Y42FeatureType | null> = this.assetPath$$.pipe(
    filter(isNotNullish),
    map(($path) => this.featureAggregationService.getServiceDefinitionByPath($path)?.featureType || null),
  );

  readonly activeTabType$: Observable<AssetSidebarTabType> = this.activeTabType$$;
  readonly params$: Observable<Partial<Params>> = this.params$$;

  readonly isOpen$: Observable<boolean> = this.assetPath$.pipe(map((assetPath) => !!assetPath));
  readonly asset$: Observable<Asset | null> = defer(() =>
    this.assetPath$.pipe(
      distinctUntilChanged(),
      tap(($path) => {
        if ($path && $path !== this.assetPath) {
          this.loading$$.next(true);
          this.setReadOnly(true);
        }
      }),
      tap((path) => (this.assetPath = path)),
      switchMap(($path) => {
        const feature = this.getFeatureServiceDef($path);
        if (!feature) {
          return of(null);
        }

        const { query } = feature;
        return query.selectUIEntity($path!) as Observable<Asset | null>;
      }),
      map((asset) => asset || null),
      tap(() => this.loading$$.next(false)),
      shareReplay({ refCount: true, bufferSize: 1 }),
      untilDestroyed(this),
    ),
  );

  readonly readOnly$: Observable<boolean> = this.readOnly$$;
  readonly loading$: Observable<boolean> = this.loading$$;

  readonly path$: Observable<AbstractFeatureUIModel['$path'] | null> = this.asset$.pipe(
    map((asset) => (asset ? asset.$path : null)),
  );

  setActiveTab(tab: AssetSidebarTabType, $path?: AbstractFeatureUIModel['$path'], params?: object) {
    this.activeTabType$$.next(tab);

    if ($path) {
      this.setActiveTab(tab, $path, params);
    }
  }

  async setActive($path: AbstractFeatureUIModel['$path']) {
    this.assetPath$$.next($path);
  }

  setReadOnly(readOnly = true) {
    this.readOnly$$.next(readOnly);
  }

  selectIsActive($path: string | undefined) {
    return this.assetPath$.pipe(map((path) => path === $path));
  }

  async updateActive(model: Partial<AbstractFeatureUIModel>) {
    const $path = this.assetPath!;
    const feature = this.getFeatureServiceDef($path);

    if (!feature) {
      return;
    }

    const { service } = feature;
    await service.update({ ...model }, $path!);
  }

  async deleteActive() {
    const $path = this.assetPath!;
    const feature = this.getFeatureServiceDef($path);

    if (!feature) {
      return;
    }

    const { service } = feature;
    await service.delete($path!);
  }

  close() {
    this.assetPath$$.next(null);
  }

  private getFeatureServiceDef($path: string | null) {
    if (!$path) {
      return;
    }

    const feature = this.featureAggregationService.getServiceDefinitionByPath($path);

    if (!feature) {
      this.errorHandlerService.handleError(`Could not find feature type for path "${$path}"`);
      return;
    }

    return feature;
  }
}
